import { useCallback, useRef, useState } from "react";

import type { AsyncSelectSearchHook } from "../BoundAsyncSelect";

/**
 * @param lazyQuery -> Apollo Lazy Query
 * @param buildVariables -> generiert alle Query-Variablen (paging + custom)
 * @param extractItems -> map edges -> nodes
 * @param extractEndCursor -> pageInfo.endCursor
 * @param extractHasNextPage -> pageInfo.hasNextPage
 */
export function useAsyncSelectCursorPaginatedLazyQuery<
	TVariables extends Record<string, unknown>,
	TData,
	TItem,
>(
	lazyQuery: (options: { variables: TVariables }) => Promise<{ data?: TData }>,
	buildVariables: (search: string, cursor: string | null) => TVariables,
	extractItems: (data: TData | undefined) => TItem[],
	extractEndCursor: (data: TData | undefined) => string | null,
	extractHasNextPage: (data: TData | undefined) => boolean,
): AsyncSelectSearchHook<TItem> {
	const [items, setItems] = useState<TItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [endCursor, setEndCursor] = useState<string | null>(null);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [searchText, setSearchText] = useState("");

	// Use refs to store latest versions of functions to avoid infinite loops
	const buildVariablesRef = useRef(buildVariables);
	buildVariablesRef.current = buildVariables;

	const lazyQueryRef = useRef(lazyQuery);
	lazyQueryRef.current = lazyQuery;

	const extractItemsRef = useRef(extractItems);
	extractItemsRef.current = extractItems;

	const extractEndCursorRef = useRef(extractEndCursor);
	extractEndCursorRef.current = extractEndCursor;

	const extractHasNextPageRef = useRef(extractHasNextPage);
	extractHasNextPageRef.current = extractHasNextPage;

	/**
	 * RUN QUERY ONLY IN AN EFFECT OR EVENT HANDLER!
	 * NEVER in render, because lazyQuery triggers network fetch.
	 */
	const execute = useCallback(async (search: string, cursor: string | null) => {
		setLoading(true);

		const variables = buildVariablesRef.current(search, cursor);
		const { data } = await lazyQueryRef.current({ variables });

		const newItems = extractItemsRef.current(data);
		const pageCursor = extractEndCursorRef.current(data);
		const pageHasNext = extractHasNextPageRef.current(data);

		if (cursor == null) {
			setItems(newItems); // replace on first page
		} else {
			setItems((prev) => [...prev, ...newItems]); // append on next pages
		}

		setEndCursor(pageCursor);
		setHasNextPage(pageHasNext);

		setLoading(false);
	}, []);

	const resetSearch = useCallback(async (): Promise<void> => {
		setSearchText("");
		return execute("", null);
	}, [execute]);

	return {
		resetSearch,
		execute,
		items,
		loading,
		hasNextPage,
		endCursor,
		searchText,
		setSearchText,
	};
}
